import { isNil } from "lodash";
import { useCallback, useEffect, useState } from "react";

import Debug from "@/react/debug";
import { errorNotification } from "@/react/notifications";

const useBackgroundQuery = ({
  backgroundQuery,
  title,
  queryData,
  data,
  isLoading,
  setTableData,
}) => {
  const [requestBackgroundRefresh, setRequestBackgroundRefresh] =
    useState(false);
  const [backgroundQueryPromise, setBackgroundQueryPromise] = useState(null);

  useEffect(() => {
    // When the query data changes, we should execute the background query (if applicable)
    Debug.log("Query data changed", queryData);
    if (isBackgroundLoading) {
      backgroundQueryPromise?.abort();
    }
    if (shouldRunBackgroundQuery(queryData)) {
      setRequestBackgroundRefresh(true);
    }
  }, [queryData]);

  const {
    trigger: backgroundQueryFn,
    queryHookResults: backgroundQueryHookResults = {},
    shouldRunQuery: shouldRunBackgroundQuery = (data) => true,
    queryArg: backgroundQueryArg,
    responseHandler: handleBackgroundQueryResponse,
  } = backgroundQuery ?? {};
  const {
    data: backgroundQueryData,
    isFetching: isBackgroundFetching,
    isLoading: isBackgroundFirstLoading,
    error: backgroundError,
  } = backgroundQueryHookResults;
  const isBackgroundLoading = isBackgroundFetching || isBackgroundFirstLoading;

  useEffect(() => {
    // We only run the background query if:
    // - it's not already running,
    // - the main query isn't running (i.e. wait for it to be over),
    // - we have a request that we should run it
    // - the given pre-conditions for running the query are met
    const runQuery =
      !isBackgroundLoading &&
      !isLoading &&
      requestBackgroundRefresh &&
      shouldRunBackgroundQuery(data);
    Debug.log(
      "runQuery?: ",
      isBackgroundLoading,
      isLoading,
      requestBackgroundRefresh,
      shouldRunBackgroundQuery(data)
    );
    if (runQuery) {
      // By setting this false here, and having it set as `true` when the primary query finishes,
      // we ensure that the background query is only run once, regardless of the other conditions in the conditional,
      setRequestBackgroundRefresh(false);
      if (backgroundQueryFn) {
        Debug.log("running background query for " + title);
        const promiseResult = backgroundQueryFn(backgroundQueryArg);
        promiseResult
          .then((response) => {
            const responseData = response?.data;
            Debug.log(
              response,
              "Received background data, old: ",
              backgroundQueryData,
              "new: ",
              responseData
            );
            if (response.isError) {
              if (response.error?.name === "AbortError") {
                Debug.log("Background query was aborted", response.error);
                return;
              }
              Debug.error("Background query received error", response.error);
              throw response.error;
            }

            // When the background query completes, then we should call the given handler
            // or just set the response as the new data, if no handler was provided
            if (handleBackgroundQueryResponse) {
              handleBackgroundQueryResponse(responseData, setTableData);
            } else if (!isNil(responseData) && responseData.length > 0) {
              // Replace the data, but only if there is some valid data to replace with
              setTableData(responseData);
            }
          })
          .catch((error) => errorNotification(error));
        setBackgroundQueryPromise(promiseResult);
      }
    }
  }, [
    isBackgroundLoading,
    isLoading,
    requestBackgroundRefresh,
    shouldRunBackgroundQuery,
    data,
    backgroundQueryFn,
    backgroundQueryArg,
    handleBackgroundQueryResponse,
  ]);

  const abortBackgroundQuery = useCallback(() => {
    Debug.log("Requesting background abort", backgroundQueryPromise);
    backgroundQueryPromise?.abort();
  }, [backgroundQueryPromise]);

  return {
    backgroundQueryData,
    isBackgroundLoading,
    abortBackgroundQuery,
    backgroundError,
    backgroundQueryHookResults,
  };
};

export default useBackgroundQuery;
